import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from '../services/axios';
import { CheckIcon, LockClosedIcon, MapIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import PageHeader from '../components/common/PageHeader';
import EmptyState from '../components/common/EmptyState';

const DEFAULT_STAGES = [
    {
        stage: 1,
        key: 'onboarding',
        title: 'Stage 1: Onboarding & Talent Profile',
        weeks: 'Week 1',
        hoursRequired: 5,
        evidence: '9-field profile complete, NDA accepted',
        signOff: 'Automatic upon 90%+ profile completion',
        route: '/profile',
        description: 'Set up your complete talent profile, define your career targets, and accept program guidelines.'
    },
    {
        stage: 2,
        key: 'learning',
        title: 'Stage 2: Core Learning & Video Training',
        weeks: 'Weeks 2 – 4',
        hoursRequired: 45,
        evidence: 'Video watch logs & MCQ quizzes (≥90% pass score)',
        signOff: 'Mentor review & system verification',
        route: '/learning',
        description: 'Master foundational AI implementation, prompt engineering, and process mapping workflows.'
    },
    {
        stage: 3,
        key: 'practical',
        title: 'Stage 3: Practice & Task Validation',
        weeks: 'Weeks 5 – 7',
        hoursRequired: 50,
        evidence: 'Tasks completed & verified practical hours log',
        signOff: 'Mentor weekly sign-off',
        route: '/tasks',
        description: 'Execute practical tasks, log your focus hours, and get feedback from your assigned mentor.'
    },
    {
        stage: 4,
        key: 'matching',
        title: 'Stage 4: Client Project Matching',
        weeks: 'Week 8',
        hoursRequired: 20,
        evidence: 'Skill match report & client brief acceptance',
        signOff: 'Program Lead assignment',
        route: '/client-project',
        description: 'Match with a real-world enterprise client project tailored to your verified skills.'
    },
    {
        stage: 5,
        key: 'execution',
        title: 'Stage 5: Client Execution & Delivery',
        weeks: 'Weeks 9 – 13',
        hoursRequired: 140,
        evidence: 'Client deliverables, weekly status reports & hours',
        signOff: 'Client Lead & Mentor co-approval',
        route: '/client-project',
        description: 'Deploy solution prototypes, interface with client stakeholders, and track implementation hours.'
    },
    {
        stage: 6,
        key: 'evaluation',
        title: 'Stage 6: Final Evaluation & Defense',
        weeks: 'Week 14',
        hoursRequired: 30,
        evidence: 'Final presentation slides, demo video & evidence vault',
        signOff: 'Evaluation panel scoring',
        route: '/evidence',
        description: 'Present your completed client implementation project to the evaluation panel for sign-off.'
    },
    {
        stage: 7,
        key: 'graduation',
        title: 'Stage 7: Graduation & Verified Certificate',
        weeks: 'Week 15',
        hoursRequired: 10,
        evidence: '300 total approved hours & verified portfolio',
        signOff: 'Platform Admin issuing verified certificate',
        route: '/achievements',
        description: 'Receive your verified digital certificate, skill transcript, and career placement recommendations.'
    }
];

const Journey = () => {
    const [progress, setProgress] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        const loadProgress = async () => {
            setLoading(true);
            setError(false);
            try {
                const response = await axios.get('/lifecycle/my-progress');
                setProgress(response.data.data);
            } catch (err) {
                try {
                    const fallback = await axios.get('/profile/onboarding/progress');
                    setProgress({
                        currentStatus: fallback.data.data.currentStatus || 'ONBOARDING',
                        displayStatus: (fallback.data.data.currentStatus || 'ONBOARDING').replace(/_/g, ' '),
                        progressPercentage: fallback.data.data.completionPercentage || 0,
                        nextAction: fallback.data.data.nextAction || 'Complete 9 Profile Fields',
                        nextRoute: '/profile',
                        isComplete: fallback.data.data.isComplete || false
                    });
                } catch (fallbackError) {
                    setError(true);
                }
            } finally {
                setLoading(false);
            }
        };
        loadProgress();
    }, []);

    if (loading) {
        return (
            <div className="space-y-4" role="status" aria-live="polite">
                <div className="skeleton h-10 w-56" />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="skeleton h-24" />
                    ))}
                </div>
                <div className="skeleton h-64" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="space-y-6">
                <PageHeader title="Unable to load journey" description="Please check your connection and try again." />
                <EmptyState
                    icon={MapIcon}
                    title="Journey unavailable"
                    description="We could not load your internship roadmap right now."
                    action={
                        <Link to="/dashboard" className="btn-primary">
                            Back to Home
                        </Link>
                    }
                />
            </div>
        );
    }

    // Determine current stage index based on progress or default to Stage 1
    const currentStageIndex = progress?.currentStageIndex || 0; // 0-indexed (Stage 1)
    const approvedHours = progress?.approvedHours || 0;
    const currentWeek = progress?.currentWeek || 1;

    return (
        <div className="w-full space-y-6 overflow-x-hidden pb-16">
            <PageHeader
                title="Journey — Full Program Roadmap"
                description="Clear stages, required hours, evidence requirements, and sign-off gates."
            />

            {/* 4 Top KPI Metric Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 animate-fade-up">
                <div className="bg-surface border border-border rounded-2xl p-5 shadow-xs flex flex-col justify-between">
                    <span className="text-xs font-semibold uppercase tracking-wider text-ink-500">Current Stage</span>
                    <div className="mt-3">
                        <p className="font-serif text-2xl font-bold text-ink-950">Stage {currentStageIndex + 1} of 7</p>
                        <p className="text-xs text-ink-600 mt-1 capitalize font-medium">
                            {DEFAULT_STAGES[currentStageIndex]?.title.split(':')[1] || 'Onboarding'}
                        </p>
                    </div>
                </div>

                <div className="bg-surface border border-border rounded-2xl p-5 shadow-xs flex flex-col justify-between">
                    <span className="text-xs font-semibold uppercase tracking-wider text-ink-500">Program Week</span>
                    <div className="mt-3">
                        <p className="font-serif text-2xl font-bold text-ink-950">Week {currentWeek} of 15</p>
                        <p className="text-xs text-ink-600 mt-1 font-medium">Active Internship Cycle</p>
                    </div>
                </div>

                <div className="bg-surface border border-border rounded-2xl p-5 shadow-xs flex flex-col justify-between">
                    <span className="text-xs font-semibold uppercase tracking-wider text-ink-500">Approved Hours</span>
                    <div className="mt-3">
                        <p className="font-serif text-2xl font-bold text-brand-700">{approvedHours} / 300 <span className="text-xs font-normal text-ink-500">hrs</span></p>
                        <div className="w-full bg-ink-100 h-2 rounded-full overflow-hidden mt-2">
                            <div className="bg-brand-600 h-full transition-all" style={{ width: `${Math.min(100, Math.round((approvedHours / 300) * 100))}%` }} />
                        </div>
                    </div>
                </div>

                <div className="bg-surface border border-border rounded-2xl p-5 shadow-xs flex flex-col justify-between">
                    <span className="text-xs font-semibold uppercase tracking-wider text-ink-500">Next Sign-off Gate</span>
                    <div className="mt-3">
                        <p className="font-serif text-xl font-bold text-ink-950">Automatic</p>
                        <p className="text-xs text-amber-900 bg-amber-50 px-2 py-1 rounded-md mt-1 inline-block font-medium">
                            Requires 90%+ Profile Completion
                        </p>
                    </div>
                </div>
            </div>

            {/* Next Step Banner */}
            <div className="bg-gradient-to-r from-brand-900 to-brand-800 text-white rounded-2xl p-6 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4 animate-fade-up">
                <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-brand-200">Recommended Action</span>
                    <h3 className="font-serif text-xl font-bold mt-1">
                        {progress?.nextAction || 'Complete your 9 required talent profile fields'}
                    </h3>
                    <p className="text-sm text-brand-100 mt-1">
                        Unlocks Stage 2 (Core Learning & Training) immediately.
                    </p>
                </div>
                <Link
                    to={progress?.nextRoute || '/profile'}
                    className="inline-flex items-center justify-center gap-2 bg-white text-brand-900 hover:bg-brand-50 text-sm font-semibold px-5 py-3 rounded-xl transition-colors shadow-xs whitespace-nowrap"
                >
                    <span>Continue Journey</span>
                    <ArrowRightIcon className="w-4 h-4" />
                </Link>
            </div>

            {/* 7-Stage Detailed Roadmap Timeline */}
            <section className="bg-surface border border-border rounded-2xl p-6 shadow-sm animate-fade-up" aria-labelledby="roadmap-heading">
                <h2 id="roadmap-heading" className="font-serif text-2xl font-bold text-ink-950 mb-6">
                    7-Stage Program Timeline
                </h2>

                <ol className="relative border-l-2 border-brand-200 ml-4 space-y-8">
                    {DEFAULT_STAGES.map((stg, idx) => {
                        const isCompleted = idx < currentStageIndex;
                        const isCurrent = idx === currentStageIndex;
                        const isLocked = idx > currentStageIndex;

                        return (
                            <li key={stg.key} className="mb-6 ml-6 group">
                                <span
                                    className={`absolute -left-3.5 flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ring-4 ring-white ${
                                        isCompleted ? 'bg-brand-700 text-white' :
                                        isCurrent ? 'bg-amber-500 text-white animate-pulse' :
                                        'bg-ink-100 text-ink-500 border border-ink-300'
                                    }`}
                                >
                                    {isCompleted ? <CheckIcon className="w-4 h-4" /> : isLocked ? <LockClosedIcon className="w-3.5 h-3.5" /> : stg.stage}
                                </span>

                                <div className={`p-5 rounded-2xl border transition-all ${
                                    isCurrent
                                        ? 'border-brand-600 bg-brand-50/30 shadow-sm'
                                        : isCompleted
                                        ? 'border-border bg-canvas/40'
                                        : 'border-border/60 bg-canvas/20 opacity-75'
                                }`}>
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-border/50 pb-3 mb-3">
                                        <div>
                                            <span className="text-xs font-semibold uppercase tracking-wider text-brand-700">{stg.weeks}</span>
                                            <h3 className="font-serif text-lg font-bold text-ink-950 mt-0.5">{stg.title}</h3>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="px-2.5 py-1 text-xs font-semibold bg-brand-100 text-brand-900 rounded-full">
                                                {stg.hoursRequired} hrs required
                                            </span>
                                            {isCurrent && (
                                                <span className="px-2.5 py-1 text-xs font-semibold bg-amber-100 text-amber-900 rounded-full">
                                                    Current Stage
                                                </span>
                                            )}
                                            {isCompleted && (
                                                <span className="px-2.5 py-1 text-xs font-semibold bg-emerald-100 text-emerald-900 rounded-full">
                                                    Completed
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    <p className="text-sm text-ink-700">{stg.description}</p>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4 pt-3 border-t border-border/40 text-xs">
                                        <div>
                                            <span className="font-semibold text-ink-950">Required Evidence: </span>
                                            <span className="text-ink-700">{stg.evidence}</span>
                                        </div>
                                        <div>
                                            <span className="font-semibold text-ink-950">Sign-off Gate: </span>
                                            <span className="text-ink-700">{stg.signOff}</span>
                                        </div>
                                    </div>

                                    {isCurrent && stg.route && (
                                        <div className="mt-4 pt-2">
                                            <Link
                                                to={stg.route}
                                                className="inline-flex items-center gap-1.5 text-sm font-bold text-brand-700 hover:text-brand-900 transition-colors"
                                            >
                                                <span>Go to {stg.title.split(':')[1]}</span>
                                                <ArrowRightIcon className="w-4 h-4" />
                                            </Link>
                                        </div>
                                    )}
                                </div>
                            </li>
                        );
                    })}
                </ol>
            </section>
        </div>
    );
};

export default Journey;
